import {
  FormMapping,
  FormStructure,
  FormValuesConstraint,
  GroupNodes,
  StepNode,
  StepNodes,
  StructureProp,
} from '../types';

/*
 * The three helpers below take `unknown` and return a typed *view* of the
 * structure. That is deliberate: the public `StructureProp<T, S>` is a deferred
 * conditional over recursive mapped types, and asking TypeScript to relate it to
 * the (also recursive) `StepNode<T>` — which is what a type predicate or a typed
 * `lodash/get` call does — expands both sides into millions of instantiations
 * and OOMs `tsc`. An `unknown` parameter skips that relation entirely.
 */

/** Returns the structure as a flat map of steps, or `undefined` if it is not one. */
export function asStepNodes<T extends FormValuesConstraint<T>>(
  obj: unknown,
): StepNodes<T> | undefined {
  if (!obj || typeof obj !== 'object') return undefined;

  const steps = obj as StepNodes<T>;
  const firstStep = Object.values(steps)[0];

  return firstStep?.component === undefined ? undefined : steps;
}

/** Returns the structure as groups of steps, or `undefined` if it is not one. */
export function asGroupNodes<T extends FormValuesConstraint<T>>(
  obj: unknown,
): GroupNodes<T> | undefined {
  if (!obj || typeof obj !== 'object') return undefined;

  const groups = obj as GroupNodes<T>;
  const firstGroup = Object.values(groups)[0];

  if (!firstGroup) return undefined;

  const firstStep = Object.values(firstGroup)[0];

  return firstStep?.component === undefined ? undefined : groups;
}

/**
 * Resolves a structure path produced by `getStructurePaths` (dot-separated keys,
 * e.g. `group1.Dummy.next.StepB`) to the node it points at.
 */
export function getStepAtPath<T extends FormValuesConstraint<T>>(
  structure: unknown,
  path: string,
): StepNode<T> | undefined {
  let node: unknown = structure;

  for (const key of path.split('.')) {
    if (!node || typeof node !== 'object') return undefined;

    node = (node as Record<string, unknown>)[key];
  }

  return node as StepNode<T> | undefined;
}

export const getStructurePaths = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  object: StructureProp<T, S>,
): string[] => {
  const allPaths: string[] = [];

  const getPath = (currPath: string, item: object) => {
    allPaths.push(currPath);

    if (Array.isArray(item)) {
      item.forEach((el, idx) => getPath(`${currPath}.${idx}`, el));
    } else if (typeof item === 'object') {
      Object.entries(item).forEach(([key, value]) => {
        getPath(`${currPath}.${key}`, value);
      });
    }
  };

  Object.entries(object).forEach(([key, value]) => {
    getPath(key, value);
  });

  return allPaths;
};

export const getStructureMapping = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  structure: StructureProp<T, S>,
) => {
  const mapping: FormMapping<T> = {
    byGroup: {},
    byStep: {},
  };

  const handleSteps = (steps: StepNodes<T>, groupName?: string) => {
    if (groupName) {
      mapping.byGroup[groupName] = mapping.byGroup[groupName] || {};
    }

    Object.keys(steps).forEach((stepName) => {
      if (groupName) {
        mapping.byGroup[groupName][stepName] = new Set<keyof T>();
      }

      mapping.byStep[stepName] = mapping.byStep[stepName] || new Set<keyof T>();

      steps[stepName].required?.forEach((fieldName) => {
        if (!mapping.byStep[stepName].has(fieldName)) {
          if (groupName) {
            mapping.byGroup[groupName][stepName].add(fieldName);
          }

          mapping.byStep[stepName].add(fieldName);
        }
      });

      // Handle child steps
      const childSteps = steps[stepName].next;

      if (childSteps && Object.keys(childSteps).length) {
        handleSteps(childSteps, groupName);
      }
    });
  };

  const steps = asStepNodes<T>(structure);

  // Multi-step form
  if (steps) {
    handleSteps(steps);

    return mapping;
  }

  // Grouped multi-step form
  const groups = asGroupNodes<T>(structure);

  if (groups) {
    Object.keys(groups).forEach((groupName) => {
      handleSteps(groups[groupName], groupName);
    });
  }

  return mapping;
};
